import { PremiumBackground } from '../effects/PremiumBackground';
import React from 'react';
import { 
Layers, 
  TrendingUp, 
  Network, 
  Building2, 
  Users2, 
  ArrowDown, 
  AlertTriangle, 
  CheckCircle2, 
  XCircle,
  HelpCircle,
  ArrowRight
} from 'lucide-react';

export interface CascadeExitProps {
  id?: string;
}

export const CascadeExit: React.FC<CascadeExitProps> = ({ id = 'exit-strategy' }) => {
  const scenarios = [
    {
      badgeNumber: '01',
      badgeTag: 'СЦЕНАРИЙ 1 // ПАКЕТНЫЙ ROLL-UP',
      clusterSub: 'Синдикация кластера до 10 кресел',
      shortEssence: 'Объединяем 2–3 клиники города под одного крупного инвестора (Family Office) с внедрением единого управления.',
      icon: Network,
      title: '1. Пакетный Roll-up (Синдикация кластера под одного инвестора)',
      howItWorks:
        'Если у вас 3 кресла, мы не продаем их в одиночку. Мы находим инвестора с чеком 100–150 млн ₽ (Family Office / Частный фонд) и собираем для него пакетную сделку из 2–3 клиник одного города (3 + 3 + 4 = 10 кресел) под единую систему управления S&P Clinic OS™.',
      advantage:
        'Выручка кластера — от 150 млн ₽, прибыль — 35 млн ₽, расходы на управление распределяются на всю сеть. Вы продаете свой бизнес в составе сильного пула с премиальным мультипликатором.',
      investorProfile: 'Family Office / Частные фонды с капиталом от 150 млн ₽',
      targetMultiplier: '5.5x – 6.8x EBITDA'
    },
    {
      badgeNumber: '02',
      badgeTag: 'СЦЕНАРИЙ 2 // ДОБОР МОЩНОСТИ (BOLT-ON)',
      clusterSub: 'Продажа крупным сетям',
      shortEssence: 'Продаем 3 кресла федералам. У них УЖЕ есть бэк-офис: ваши кресла дают чистый предельный доход без затрат.',
      icon: TrendingUp,
      title: '2. Продажа как Bolt-on (Добор мощности для федеральных сетей)',
      howItWorks:
        'Мы продаем небольшую клинику стратегическому покупателю — федеральной сети или крупному региональному игроку (Медси, СОГАЗ, сетевые стоматологии), выходящему в ваш район.',
      advantage:
        'У сети УЖЕ ЕСТЬ главный бухгалтер, колл-центр, юристы и маркетинг. Им не нужно нанимать управленцев на ваши 3 кресла. Ваша выручка падает на их готовую инфраструктуру, обеспечивая инвестору 100% предельную маржинальность.',
      investorProfile: 'Федеральные медицинские холдинги и сетевые операторы',
      targetMultiplier: 'Предельный Cash-out в день закрытия'
    },
    {
      badgeNumber: '03',
      badgeTag: 'СЦЕНАРИЙ 3 // МОДЕЛЬ MSO',
      clusterSub: 'Внешняя управляющая компания',
      shortEssence: 'Инвестор покупает активы, а управление берет на себя наша MSO-структура на абонентском обслуживании (Shared Team).',
      icon: Building2,
      title: '3. Модель MSO (Централизованное абонентское управление)',
      howItWorks:
        'Американская модель MSO (Management Services Organization). Частный инвестор покупает клинику, но операционное управление передает нашему аккредитованному оператору.',
      advantage:
        'Наша команда ведет пул из 4–5 небольших клиник параллельно. Для одной клиники расход на управление составляет разумный фикс (150–200 тыс. ₽/мес), а инвестор получает пассивный доход без риска развала процессов.',
      investorProfile: 'Непрофильные частные инвесторы, ищущие пассивный доход',
      targetMultiplier: 'Фиксированный абонентский фикс 150–200 тыс. ₽'
    },
    {
      badgeNumber: '04',
      badgeTag: 'СЦЕНАРИЙ 4 // СДЕЛКА 80/20 (ROLLOVER)',
      clusterSub: 'Фаундер остается младшим партнером',
      shortEssence: 'Инвестор забирает 80% кэшем, основатель сохраняет 20% и пост Главврача с опционом выкупа остатка через 2–3 года.',
      icon: Users2,
      title: '4. Сделка 80/20 (Rollover Equity — Основатель остается партнером)',
      howItWorks:
        'Инвестор забирает 80% долей (вы получаете гарантированный Cash-out), а вы сохраняете 20% бизнеса и статус Главного врача с высоким окладом и процентом от прибыли.',
      advantage:
        'Инвестор спокоен за преемственность медицины, а вы освобождаетесь от рутины, налогов и кассовых разрывов. В договор вшивается опцион: через 2–3 года инвестор обязан выкупить ваши оставшиеся 20% по заранее согласованной формуле.',
      investorProfile: 'Стратегические фонды с горизонтом интеграции 2–3 года',
      targetMultiplier: '80% Cash-out сразу + Рост оставшихся 20%'
    },
  ];

  return (
    <section
      id={id}
      aria-label="Экономика масштаба и каскадный экзит"
      className="relative w-full bg-[#FAF8F5] text-[#141416] py-24 md:py-32 border-t border-[#EAE5DE] font-sans overflow-hidden"
    >
      {/* ТАКТИЛЬНЫЙ МАКРО-РЕЛЬЕФ */}
      <PremiumBackground variant={9} />

      
      

      {/* Light subtle archival paper gradient */}
      <div className="absolute top-0 left-0 right-0 h-48 bg-gradient-to-b from-[#F2EDE4]/60 to-transparent pointer-events-none" />

      <div className="relative z-10 max-w-[1400px] mx-auto px-4 sm:px-6 md:px-8 lg:px-12">
        
        {/* =========================================================================
            1. НАДЗАГОЛОВОК, 2. ЗАГОЛОВОК H2, 3. ПОДЗАГОЛОВОК-МАНИФЕСТ
            ========================================================================= */}
        <div className="max-w-4xl mb-14 flex flex-col gap-6">
          <div className="luxury-eyebrow text-[#8C6D37] flex items-center gap-2.5 font-bold tracking-[0.2em] uppercase">
            <span className="w-1.5 h-1.5 rounded-none bg-[#8C6D37]" />
            ЭТАЖ 10 // СТРАТЕГИЯ ЛИКВИДНОСТИ // M&A-ИНЖЕНЕРИЯ
          </div>

          <h2 className="text-3xl sm:text-5xl lg:text-[54px] font-serif font-semibold text-[#141416] tracking-tight leading-[1.1]">
            Решение дилеммы масштаба: <br />
            <span className="text-[#64748B] italic font-serif">как мы продаем клиники от 3-х кресел по премиальной цене</span>
          </h2>

          <div className="text-[16px] sm:text-[17px] text-[#334155] leading-[1.65] font-sans font-normal space-y-3 max-w-3xl border-l-[4px] border-[#8C6D37] pl-6 mt-4">
            <p>
              Обычный инвестор «с улицы» не справится с управлением небольшой клиникой, а наемная команда топ-менеджеров съест всю ее прибыль.
            </p>
            <p className="text-[#141416] font-bold">
              Мы не питаем иллюзий. Мы проектируем сделку по законам институциональной экономики, превращая локальную практику в желанный актив для крупного капитала.
            </p>
          </div>
        </div>

        {/* =========================================================================
            4. ЭКОНОМИЧЕСКИЙ РАЗБОР ТУПИКА: «3 КРЕСЛА ПРОТИВ 10 КРЕСЕЛ»
            ========================================================================= */}
        <div className="bg-white rounded-[2px] p-6 sm:p-8 md:p-10 mb-14 border border-[#EAE5DE] shadow-xs">
          <div className="flex items-center gap-2.5 mb-6 text-[#141416]">
            <AlertTriangle className="w-5 h-5 text-[#8C6D37] shrink-0" />
            <h3 className="text-xl sm:text-2xl font-serif font-semibold text-[#141416] tracking-tight">
              Сухая математика, о которой молчат обычные брокеры:
            </h3>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start mb-8">
            {/* Текстовая аргументация */}
            <div className="lg:col-span-7 space-y-4 text-[15px] text-[#334155] leading-[1.65] font-normal">
              <p>
                Одиночная клиника на <strong className="text-[#141416] font-bold">3 кресла</strong> генерирует 
                <strong className="text-[#141416] font-bold"> 35–50 млн ₽ выручки</strong> и около 
                <strong className="text-[#141416] font-bold"> 7–9 млн ₽ чистой прибыли</strong> в год.
              </p>
              <p>
                Стоимость сильной управляющей команды (наемный директор, финансовый контролер, начмед и маркетолог) 
                составляет минимум <strong className="text-[#141416] font-bold">600 000 ₽ в месяц</strong> ⟹ 
                около <strong className="text-rose-700 font-bold">8–9 млн ₽ в год с налогами</strong>.
              </p>
              <div className="p-4 bg-[#8C6D37]/10 border border-[#8C6D37]/40 rounded-[2px] text-[#141416] text-[14px]">
                <strong className="font-bold block mb-1 font-mono text-[12px] uppercase tracking-wider text-[#8C6D37]">Парадокс кустарного рынка:</strong>
                Если частный инвестор покупает 3 кресла и нанимает профессионалов, управляющая команда обнуляет 
                <strong className="font-bold text-[#141416]"> 100% чистой прибыли</strong>. Бизнес теряет инвестиционный смысл.
              </div>
              <p className="text-[#141416] font-bold pt-1">
                Реальная окупаемость наемного менеджмента начинается от <strong className="text-[#8C6D37] font-bold">8–10 кресел</strong> (выручка от 120 млн ₽), 
                где расходы на управление составляют лишь 10–15% от операционной прибыли.
              </p>
            </div>

            {/* Наглядная сравнительная плашка (3 кресла vs 10 кресел) */}
            <div className="lg:col-span-5 grid grid-cols-1 gap-4">
              {/* 3 кресла: Тупик */}
              <div className="bg-rose-50 border border-rose-200 p-5 rounded-[2px] relative overflow-hidden shadow-xs">
                <div className="flex items-center justify-between gap-2 mb-3">
                  <span className="font-mono text-[11px] text-rose-700 font-bold uppercase tracking-wider flex items-center gap-1.5">
                    <XCircle className="w-4 h-4 text-rose-600" />
                    ТУПИК: 3 КРЕСЛА (ОДИНОЧНЫЙ АКТИВ)
                  </span>
                  <span className="font-mono text-[11px] text-rose-800 font-bold bg-white px-2 py-0.5 rounded-[2px] border border-rose-200">
                    Прибыль: 0 ₽
                  </span>
                </div>
                <div className="space-y-1.5 font-mono text-[12px] text-[#64748B]">
                  <div className="flex justify-between">
                    <span>Выручка:</span>
                    <span className="font-bold text-[#141416]">35–50 млн ₽</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Чистая прибыль:</span>
                    <span className="font-bold text-[#141416]">+7–9 млн ₽</span>
                  </div>
                  <div className="flex justify-between text-rose-600">
                    <span>Наемный бэк-офис:</span>
                    <span className="font-bold">-8–9 млн ₽</span>
                  </div>
                  <div className="pt-2 border-t border-rose-200 flex justify-between font-bold text-rose-700">
                    <span>Итог инвестора:</span>
                    <span>ОБНУЛЕНИЕ 100% ПРИБЫЛИ</span>
                  </div>
                </div>
              </div>

              {/* 10 кресел: Институциональный масштаб */}
              <div className="bg-[#8C6D37]/10 border border-[#8C6D37]/40 p-5 rounded-[2px] relative overflow-hidden shadow-xs">
                <div className="flex items-center justify-between gap-2 mb-3">
                  <span className="font-mono text-[11px] text-[#8C6D37] font-bold uppercase tracking-wider flex items-center gap-1.5">
                    <CheckCircle2 className="w-4 h-4 text-[#8C6D37]" />
                    МАСШТАБ: 8–10 КРЕСЕЛ (КЛАСТЕР / СЕТЬ)
                  </span>
                  <span className="font-mono text-[11px] text-[#8C6D37] font-bold bg-white px-2 py-0.5 rounded-[2px] border border-[#8C6D37]/30">
                    Маржа: 100%
                  </span>
                </div>
                <div className="space-y-1.5 font-mono text-[12px] text-[#334155]">
                  <div className="flex justify-between">
                    <span>Выручка:</span>
                    <span className="font-bold text-[#141416]">от 120–150 млн ₽</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Чистая прибыль:</span>
                    <span className="font-bold text-[#141416]">+30–35 млн ₽</span>
                  </div>
                  <div className="flex justify-between text-[#8C6D37]">
                    <span>Доля управления:</span>
                    <span className="font-bold">всего 10–15% прибыли</span>
                  </div>
                  <div className="pt-2 border-t border-[#8C6D37]/30 flex justify-between font-bold text-[#141416]">
                    <span>Итог инвестора:</span>
                    <span className="text-[#8C6D37]">ВЫСОКАЯ КАПИТАЛИЗАЦИЯ</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* =========================================================================
            5. ЧЕТЫРЕ ИНСТИТУЦИОНАЛЬНЫХ СЦЕНАРИЯ ВЫХОДА (2x2 CARDS GRID)
            ========================================================================= */}
        <div className="mb-14">
          <div className="flex items-center justify-between mb-8 pb-4 border-b border-[#EAE5DE]">
            <div>
              <span className="luxury-eyebrow text-[#8C6D37] block mb-1 font-bold tracking-wider uppercase">
                АРХИТЕКТУРА СДЕЛКИ S&P
              </span>
              <h3 className="text-2xl sm:text-3xl font-serif font-semibold text-[#141416] tracking-tight">
                Четыре институциональных сценария выхода
              </h3>
            </div>
            <div className="hidden sm:flex items-center gap-2 font-mono text-[11px] text-[#64748B] bg-[#F7F4EE] px-3 py-1.5 rounded-[2px] border border-[#EAE5DE] font-bold">
              <Layers className="w-3.5 h-3.5 text-[#8C6D37]" />
              ИНЖЕНЕРИЯ S&P DESK
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {scenarios.map((sc, idx) => {
              const IconComp = sc.icon;
              return (
                <div
                  key={idx}
                  className="bg-white rounded-[2px] p-7 sm:p-8 flex flex-col justify-between transition-all duration-300 border border-[#EAE5DE] hover:border-[#CBD5E1] shadow-xs group"
                >
                  <div>
                    {/* Header of the card */}
                    <div className="flex items-start justify-between gap-4 mb-5 pb-5 border-b border-[#EAE5DE]">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-[2px] bg-[#F7F4EE] border border-[#EAE5DE] group-hover:border-[#8C6D37]/50 flex items-center justify-center text-[#8C6D37] transition-colors">
                          <IconComp className="w-5 h-5" />
                        </div>
                        <div>
                          <span className="font-mono text-[10px] text-[#8C6D37] uppercase tracking-widest font-bold block">
                            {sc.badgeTag}
                          </span>
                          <span className="text-[12px] text-[#64748B] font-normal">
                            ({sc.clusterSub})
                          </span>
                        </div>
                      </div>
                      <span className="font-mono text-[13px] text-[#64748B] font-bold">
                        {sc.badgeNumber}
                      </span>
                    </div>

                    {/* Quick essence callout */}
                    <div className="p-3.5 bg-[#F7F4EE] border-l-2 border-[#8C6D37] rounded-r-[2px] mb-6 text-[13px] text-[#141416] font-bold leading-snug shadow-xs">
                      {sc.shortEssence}
                    </div>

                    {/* Full explanation */}
                    <div className="space-y-4 text-[14px] leading-relaxed">
                      <div>
                        <span className="font-mono text-[11px] uppercase tracking-wider text-[#141416] font-bold block mb-1.5">
                          Как это работает:
                        </span>
                        <p className="text-[#334155] font-normal">
                          {sc.howItWorks}
                        </p>
                      </div>

                      <div className="pt-2">
                        <span className="font-mono text-[11px] uppercase tracking-wider text-[#8C6D37] font-bold block mb-1.5">
                          Выгода и преимущество:
                        </span>
                        <p className="text-[#141416] font-bold">
                          {sc.advantage}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Card bottom telemetry */}
                  <div className="mt-8 pt-5 border-t border-[#EAE5DE] flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs font-mono">
                    <div className="text-[#64748B]">
                      <span className="text-[#64748B] block text-[10px] uppercase tracking-wider font-bold">Инвестор:</span>
                      <span className="text-[#141416] font-bold">{sc.investorProfile}</span>
                    </div>
                    <div className="sm:text-right">
                      <span className="text-[#64748B] block text-[10px] uppercase tracking-wider font-bold">Оценка:</span>
                      <span className="text-[#8C6D37] font-bold">{sc.targetMultiplier}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* =========================================================================
            6. ИТОГОВЫЙ ИНСТИТУЦИОНАЛЬНЫЙ ВЫВОД (CALLOUT BOX)
            ========================================================================= */}
        <div className="bg-[#8C6D37]/10 rounded-[2px] border-l-4 border-l-[#8C6D37] border-y border-r border-[#8C6D37]/40 p-6 sm:p-8 mb-16 shadow-xs">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-[2px] bg-white border border-[#8C6D37]/40 flex items-center justify-center text-[#8C6D37] shrink-0 mt-0.5 shadow-xs">
              <span className="text-xl">💡</span>
            </div>
            <div>
              <h4 className="text-lg sm:text-xl font-serif font-semibold text-[#141416] mb-2 tracking-tight">
                Принцип гарантированной ликвидности Structure & Partners:
              </h4>
              <p className="text-[15px] text-[#334155] leading-relaxed font-normal mb-2">
                Мы не выставляем клиники на продажу «на удачу».
              </p>
              <p className="text-[15px] text-[#141416] font-bold leading-relaxed">
                Мы заранее знаем профиль каждого типа покупателя и подбираем под масштаб вашего бизнеса ту финансовую конструкцию, которая обеспечит закрытие сделки по рыночной цене без риска дефолта после передачи ключей.
              </p>
            </div>
          </div>
        </div>

        {/* =========================================================================
            7. НАВИГАТОР ПЕРЕХОДА К СЛЕДУЮЩЕМУ ЭКРАНУ
            ========================================================================= */}
        <div className="text-center pt-2">
          <a
            href="#confidentiality"
            className="inline-flex items-center gap-2 font-mono text-[12px] text-[#64748B] hover:text-[#141416] transition-all py-3 px-6 rounded-[2px] hover:bg-black/5 border border-transparent hover:border-[#EAE5DE] group tracking-tight font-bold"
          >
            <span>[ ПЕРЕЙТИ К ЭТАЖУ 11: АРХИТЕКТУРА КОНФИДЕНЦИАЛЬНОСТИ ↓ ]</span>
          </a>
        </div>

      </div>
    </section>
  );
};
